import { z } from 'zod';

const optionalUrl = z
  .string()
  .trim()
  .max(500, 'A URL deve ter no máximo 500 caracteres.')
  .refine((value) => {
    if (!value) return true;
    try {
      const parsed = new URL(value);
      return ['http:', 'https:'].includes(parsed.protocol) && Boolean(parsed.hostname);
    } catch {
      return false;
    }
  }, 'Informe uma URL começando com http:// ou https://.');

export const agentRequestSchema = z.object({
  requester: z.object({
    name: z.string().trim().min(2, 'Informe seu nome completo.').max(150),
    email: z.string().trim().email('Informe um e-mail válido.').max(254),
    phone: z
      .string()
      .trim()
      .max(30)
      .refine((value) => {
        const trimmed = value.trim();
        if ((trimmed.startsWith('+') && !trimmed.startsWith('+55')) || (trimmed.startsWith('00') && !trimmed.startsWith('0055'))) return false;
        let digits = trimmed.replace(/\D/g, '');
        if (digits.startsWith('00')) digits = digits.slice(2);
        if (digits.startsWith('55')) digits = digits.slice(2);
        return (digits.length === 10 || digits.length === 11) && digits[0] !== '0' && digits[1] !== '0';
      }, 'Informe um telefone brasileiro válido com DDD.'),
    role: z.string().trim().max(150).optional(),
  }),
  company: z.object({
    name: z.string().trim().min(2, 'Informe o nome da empresa.').max(200),
    segment: z.string().trim().min(2, 'Informe o segmento da empresa.').max(150),
    website: optionalUrl,
    monthlyConversationVolume: z.preprocess((value) => (value === '' || value === null ? undefined : Number(value)), z.number().int().min(0).max(2147483647, 'Informe um volume mensal menor.').optional()),
  }),
  agent: z
    .object({
      name: z.string().trim().min(2, 'Dê um nome para o agente.').max(150),
      channels: z.array(z.enum(['WHATSAPP', 'WEB_CHAT', 'INSTAGRAM', 'VOICE', 'API', 'OTHER'])).min(1, 'Selecione pelo menos um canal.'),
      otherChannel: z.string().trim().max(150).optional(),
    })
    .superRefine((agent, context) => {
      if (agent.channels.includes('OTHER') && !agent.otherChannel) {
        context.addIssue({
          code: 'custom',
          path: ['otherChannel'],
          message: 'Descreva o outro canal.',
        });
      }
    }),
  knowledge: z.object({
    context: z.string().trim().max(10000).optional(),
    urls: z.array(optionalUrl).max(10),
  }),
  conversationExamples: z
    .array(
      z.object({
        customerMessage: z.string().trim().min(1, 'Informe a mensagem do cliente.').max(5000),
        idealAgentResponse: z.string().trim().min(1, 'Informe a resposta ideal.').max(5000),
        sortOrder: z.number().int().min(0).max(2147483647),
      })
    )
    .max(20),
  integrations: z
    .array(
      z.object({
        name: z.enum(['CRM', 'CALENDAR', 'ERP', 'ECOMMERCE', 'PAYMENTS', 'HUMAN_HANDOFF', 'OTHER']),
        purpose: z.string().trim().max(2000),
        sortOrder: z.number().int().min(0).max(2147483647),
      })
    )
    .max(20),
  finalSummary: z.string().trim().min(30, 'Conte com mais detalhes o que o agente deve fazer.').max(10000),
  serviceLevel: z.string().refine((value) => ['PRIORITY', 'FREE'].includes(value), 'Escolha uma modalidade.'),
  priorityConfigVersion: z.string().max(100).optional(),
  consentAccepted: z.literal(true, {
    errorMap: () => ({
      message: 'Você precisa autorizar o processamento dos dados.',
    }),
  }),
  honeypot: z.string().max(0).optional(),
});

export const defaultAgentRequest = {
  requester: { name: '', email: '', phone: '', role: '' },
  company: {
    name: '',
    segment: '',
    website: '',
    monthlyConversationVolume: '',
  },
  agent: { name: '', channels: ['WHATSAPP'], otherChannel: '' },
  knowledge: { context: '', urls: [''] },
  conversationExamples: [],
  integrations: [],
  finalSummary: '',
  serviceLevel: '',
  priorityConfigVersion: '',
  consentAccepted: false,
  honeypot: '',
};

export const stepFields = [['requester'], ['company'], ['agent'], ['knowledge'], ['conversationExamples'], ['integrations'], ['finalSummary', 'consentAccepted'], ['serviceLevel']];
